import { useRef, useEffect, useState } from "react";
import { toast } from "sonner";
import { XIcon, DownloadSimpleIcon, CopyIcon, XLogoIcon, TelegramLogoIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { stampBrandApe } from "../lib/shareLogo";

const NEON = "#00E599", DANGER = "#FF3366", WARN = "#F5A623", MUTED = "#8b8b8b";
const short = (a) => (a && a.length > 10 ? `${a.slice(0, 5)}…${a.slice(-4)}` : a || "");
const label = (w) => w.label || short(w.address);
const scoreColor = (s) => (s >= 60 ? NEON : s >= 35 ? WARN : DANGER);

export const LeaderboardShareCard = ({ wallets = [], open, onClose }) => {
  const canvasRef = useRef(null);
  const [copying, setCopying] = useState(false);
  const top = wallets.slice(0, 5);

  const shareUrl = "https://alphaapeing.com/leaderboard";
  const shareText = top.length
    ? `🏆 Top Solana smart-money wallets by on-chain PnL:\n#1 ${label(top[0])} · ${top[0].score} score · ${top[0].winRate}% win rate · ${top[0].totalPnlSol >= 0 ? "+" : ""}${top[0].totalPnlSol} SOL\nFind & copy proven traders on AlphaApeing 🦍`
    : "🏆 Top Solana smart-money wallets by on-chain PnL — AlphaApeing";

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const W = 1080, H = 1080;
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.035)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    ctx.strokeStyle = "rgba(0,229,153,0.30)";
    ctx.lineWidth = 4;
    ctx.strokeRect(24, 24, W - 48, H - 48);

    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.font = "800 44px Arial, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.fillText("ALPHA", 70, 116);
    const w1 = ctx.measureText("ALPHA").width;
    ctx.fillStyle = NEON;
    ctx.fillText("APEING", 70 + w1, 116);

    ctx.font = "800 62px Arial, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.fillText("🏆 TOP SMART MONEY", 70, 210);
    ctx.font = "600 24px monospace";
    ctx.fillStyle = MUTED;
    ctx.fillText("Best Solana wallets · verified on-chain PnL", 72, 250);

    // column header
    ctx.font = "600 22px monospace";
    ctx.fillStyle = MUTED;
    ctx.fillText("#  WALLET", 70, 320);
    ctx.textAlign = "right";
    ctx.fillText("SCORE", 690, 320);
    ctx.fillText("WIN", 830, 320);
    ctx.fillText("PnL (SOL)", W - 70, 320);
    ctx.textAlign = "left";

    const medals = ["🥇", "🥈", "🥉", "4", "5"];
    let y = 380;
    top.forEach((w, i) => {
      const rowH = 118;
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(70, y + rowH - 24); ctx.lineTo(W - 70, y + rowH - 24); ctx.stroke();

      ctx.textAlign = "left";
      ctx.font = "700 40px Arial, sans-serif";
      ctx.fillStyle = i < 3 ? "#fff" : MUTED;
      ctx.fillText(medals[i], 70, y + 44);
      ctx.font = "700 40px monospace";
      ctx.fillStyle = "#fff";
      ctx.fillText(label(w), 150, y + 44);

      ctx.textAlign = "right";
      ctx.font = "800 44px Arial, sans-serif";
      ctx.fillStyle = scoreColor(w.score);
      ctx.fillText(String(w.score), 690, y + 46);

      ctx.font = "600 34px monospace";
      ctx.fillStyle = "#fff";
      ctx.fillText(`${w.winRate}%`, 840, y + 44);

      ctx.font = "800 40px Arial, sans-serif";
      ctx.fillStyle = w.totalPnlSol >= 0 ? NEON : DANGER;
      ctx.fillText(`${w.totalPnlSol >= 0 ? "+" : ""}${w.totalPnlSol}`, W - 70, y + 46);

      ctx.textAlign = "left";
      y += rowH;
    });

    // footer
    ctx.textAlign = "left";
    ctx.fillStyle = MUTED;
    ctx.font = "600 22px monospace";
    ctx.fillText("Research tool · not financial advice", 70, 1012);
    ctx.textAlign = "right";
    ctx.fillStyle = NEON;
    ctx.font = "700 30px monospace";
    ctx.fillText("alphaapeing.com/leaderboard", W - 70, 1012);
    ctx.textAlign = "left";
    stampBrandApe(c);
  }, [open, wallets]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const getBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "alphaapeing-top-smart-money.png"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Leaderboard card downloaded");
  };

  const copy = async () => {
    setCopying(true);
    try {
      const blob = await getBlob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Image copied — paste it into your post");
    } catch {
      toast.error("Copy not supported here — use Download instead");
    }
    setCopying(false);
  };

  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const shareTg = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, "_blank");

  return (
    <div data-testid="leaderboard-share-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-white/15 max-w-md w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Share the leaderboard</h3>
          <button data-testid="leaderboard-share-close" onClick={onClose} className="p-1.5 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>
        <div className="border border-white/10 overflow-hidden">
          <canvas ref={canvasRef} width={1080} height={1080} className="w-full h-auto block" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="leaderboard-download-btn" onClick={download} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors">
            <DownloadSimpleIcon size={16} /> Download
          </button>
          <button data-testid="leaderboard-copy-btn" onClick={copy} disabled={copying} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors disabled:opacity-50">
            {copying ? <SpinnerGapIcon size={16} className="animate-spin" /> : <CopyIcon size={16} />} Copy image
          </button>
          <button data-testid="leaderboard-x-btn" onClick={shareX} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors">
            <XLogoIcon size={16} weight="fill" /> Share on X
          </button>
          <button data-testid="leaderboard-tg-btn" onClick={shareTg} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#229ED9] text-white text-sm font-semibold hover:bg-[#229ED9]/90 transition-colors">
            <TelegramLogoIcon size={16} weight="fill" /> Telegram
          </button>
        </div>
      </div>
    </div>
  );
};
